import { Badge, Box, Dialog, IconButton, Typography } from '@mui/material'
import { CustomLoadingOverlay, getStringId } from 'components/common/AgGridUtils'
import { useCallback, useMemo, useRef, useState } from 'react'
import useUnits, { UNITS_FOR } from 'components/common/hooks/useUnits'

import AddAlertDialog from './AddAlertDialog'
import { AgGridReact } from 'ag-grid-react'
import AlertsRibbon from './AlertsRibbon'
import CloseIcon from '@mui/icons-material/Close'
import ConfirmDialog from 'components/common/ConfirmDialog'
import CopyAlertDialog from './CopyAlertDialog'
import FavoriteWellsModal from './FavoriteWellsModal'
import { Icon as Iconify } from '@iconify/react'
import StatusSnackbar from 'components/common/StatusSnackbar'
import { appColors } from 'utils'
import { numberWithCommasDecimals } from 'utils/stringFunctions'
import useAlerts from 'components/common/hooks/useAlerts'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

const AlertsModal = ({
  onCancel,
  byUser,
  byRole,
  userName,
  roleId,
  alertUsers,
  alertRoles,
  alertGroup,
  alertGroupAlerts,
  alertGroups,
}) => {
  const {
    loading,
    alertData,
    updateAlertsByRole,
    updateAlertsByUser,
    phaseData,
    rigData,
    getAlertsKey,
    updateAlertsByAlertGroup,
  } = useAlerts(byUser, byRole, userName, roleId, alertGroupAlerts)
  const gridApi = useRef(null)
  const gridApiDetails = useRef(null)
  const headersRef = useRef(null)
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    mode: 'add',
  })
  const [copyOpen, setCopyOpen] = useState(false)
  const [status, setStatus] = useState({ show: false, severity: 'info', message: '' })
  const isRolesMode = Array.isArray(alertRoles) && alertRoles.length > 0
  const isUsersMode = !isRolesMode && Array.isArray(alertUsers) && alertUsers.length > 0
  const isGroupsMode = !isRolesMode && !isUsersMode && Array.isArray(alertGroups) && alertGroups.length > 0

  const copyButtonLabel = isRolesMode
    ? 'Copy From Roles'
    : isUsersMode
      ? 'Copy From Users'
      : isGroupsMode
        ? 'Copy From Groups'
        : ''
  const showCopyButton = isRolesMode || isUsersMode || isGroupsMode
  const { getAgGridTheme, getWindowBarColor } = useInnovaTheme()
  const isDeleting = useRef(false)
  const { getUnitsText } = useUnits()
  const [selectedAlertHeader, setSelectedAlertHeader] = useState(null)
  const [selectedAlert, setSelectedAlert] = useState(null)
  const [changesMade, setChangesMade] = useState(false)
  const canAdd = Array.isArray(selectedAlertHeader?.dropDowns) && selectedAlertHeader.dropDowns.length > 0
  const [confirm, setConfirm] = useState({ show: false })
  const [favoriteWellsOpen, setFavoriteWellsOpen] = useState(false)

  const copySourceOptions = useMemo(() => {
    const list = isRolesMode ? alertRoles : isUsersMode ? alertUsers : isGroupsMode ? alertGroups : []
    if (!Array.isArray(list) || list.length === 0) return []

    if (isGroupsMode) {
      return list
        .filter((x) => !!x?.groupName)
        .map((x) => ({
          id: x.id ?? `${x.groupName}`,
          label: `${x.groupName}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    if (isUsersMode) {
      return list
        .filter((x) => !!x?.userName)
        .map((x) => ({
          id: x.userName,
          label: x.userName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    return list
      .filter((x) => !!x?.organization && !!x?.name)
      .map((x) => ({
        id: x.id ?? `${x.organization}:${x.name}`,
        label: `${x.organization}:${x.name}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [alertRoles, alertUsers, alertGroups, isRolesMode, isUsersMode, isGroupsMode])

  const refreshTypeCellForRow = useCallback((type) => {
    if (!gridApi.current) return

    const rowId = getStringId(type)
    const rowNode = gridApi.current.getRowNode(rowId)
    if (!rowNode) return

    gridApi.current.refreshCells({
      rowNodes: [rowNode],
      columns: ['type'],
      force: true,
    })
  }, [])

  const onSave = useCallback(async () => {
    gridApi.current?.stopEditing()
    gridApiDetails.current?.stopEditing()

    if (alertGroup) {
      const res = await updateAlertsByAlertGroup(alertData, alertGroup.uid)
      showResult(res)
      onCancel()
      return
    }

    if (!byUser) {
      const res = await updateAlertsByRole(alertData)
      showResult(res)
      onCancel()
      return
    }

    const res = await updateAlertsByUser(alertData)
    showResult(res)
    onCancel()
  }, [alertData, byUser, updateAlertsByRole, updateAlertsByUser, updateAlertsByAlertGroup, onCancel, alertGroup])

  const onDiscard = useCallback(() => {
    setChangesMade(false)
    onCancel()
  }, [onCancel])

  const onAddAlert = useCallback(() => {
    if (!selectedAlertHeader) return

    setAlertDialog({
      open: true,
      mode: 'add',
    })
  }, [selectedAlertHeader])

  const updateAlertHeader = useCallback(
    (nextHeader) => {
      if (!Array.isArray(alertData?.notifications)) return

      const headerIndex = alertData.notifications.findIndex((header) => header.type === nextHeader.type)

      if (headerIndex !== -1) {
        alertData.notifications[headerIndex] = nextHeader
      }

      gridApi.current?.getRowNode(nextHeader.type)?.setData(nextHeader)
      setSelectedAlertHeader(nextHeader)
    },
    [alertData],
  )

  const onEditAlert = useCallback(() => {
    if (!selectedAlertHeader || !selectedAlert) return

    setAlertDialog({
      open: true,
      mode: 'edit',
    })
  }, [selectedAlertHeader, selectedAlert])

  const updateDepthColumnVisibility = useCallback((alerts) => {
    const showMinDepth = alerts.some((alert) => alert.useMinDepth)
    const showMaxDepth = alerts.some((alert) => alert.useMaxDepth)

    gridApiDetails.current?.setColumnsVisible(['minDepth'], showMinDepth)
    gridApiDetails.current?.setColumnsVisible(['maxDepth'], showMaxDepth)
  }, [])

  const onDeleteAlert = useCallback(() => {
    if (!selectedAlertHeader || !selectedAlert) return
    if (isDeleting.current) return

    isDeleting.current = true

    const nextAlerts = (selectedAlertHeader.alerts || []).filter((x) => x.uuid !== selectedAlert.uuid)

    const nextHeader = {
      ...selectedAlertHeader,
      alerts: nextAlerts,
    }

    updateAlertHeader(nextHeader)
    setSelectedAlert(nextAlerts[0] || null)

    refreshTypeCellForRow(nextHeader.type)
    gridApiDetails.current?.setGridOption('rowData', nextAlerts)
    updateDepthColumnVisibility(nextAlerts)

    setChangesMade(true)
    isDeleting.current = false
  }, [selectedAlertHeader, selectedAlert, refreshTypeCellForRow, updateDepthColumnVisibility, updateAlertHeader])

  const defaultColDef = useMemo(() => {
    return {
      headerClass: 'header-no-padding',
      resizable: true,
      sortable: false,
      editable: false,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [])

  const defaultColDefDetails = useMemo(() => {
    return {
      suppressHeaderMenuButton: false,
      resizable: true,
      sortable: true,
      editable: false,
      autoHeight: true,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
      readOnly: true,
    }
  }, [])

  const getEditable = useCallback(
    (data) => {
      if (data?.source === 'role' && byUser) return false

      return true
    },
    [byUser],
  )

  const onGridReady = (params) => {
    gridApi.current = params.api
  }

  const onGridReadyDetails = (params) => {
    gridApiDetails.current = params.api
  }

  const onFirstDataRendered = (params) => {
    autoSizeColumns()

    const firstNode = params.api.getDisplayedRowAtIndex(0)
    if (firstNode) {
      firstNode.setSelected(true)
      params.api.ensureIndexVisible(0)
    }
  }

  const onFirstDataRenderedDetails = (params) => {
    if (gridApiDetails.current) autoSizeColumnsDetails()

    const firstNode = params.api.getDisplayedRowAtIndex(0)
    if (firstNode) {
      firstNode.setSelected(true)
      params.api.ensureIndexVisible(0)
    }
    setSelectedAlert(firstNode ? firstNode.data : null)
  }

  const autoSizeColumns = () => {
    if (gridApi.current === null) return
    if (gridApi.current.isDestroyed()) return
    setTimeout(() => {
      gridApi.current.autoSizeAllColumns()
    }, 100)
  }

  const autoSizeColumnsDetails = () => {
    if (gridApiDetails.current === null) return
    if (gridApiDetails.current.isDestroyed()) return
    gridApiDetails.current?.autoSizeAllColumns()
  }

  const centerAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    [],
  )

  const gridOptions = {
    loadingOverlayComponent: CustomLoadingOverlay,
    onCellValueChanged: () => {
      setChangesMade(true)
    },
    onSelectionChanged: (params) => {
      const selectedRows = params.api.getSelectedRows()
      const selectedHeader = selectedRows.length ? selectedRows[0] : null
      const alerts = selectedHeader?.alerts || []

      setSelectedAlertHeader(selectedHeader)
      setSelectedAlert(alerts[0] || null)

      setTimeout(() => {
        updateDepthColumnVisibility(alerts)
      })
    },
  }

  const gridOptionsDetails = {
    loadingOverlayComponent: CustomLoadingOverlay,
    onSelectionChanged: (params) => {
      const selectedRows = params.api.getSelectedRows()
      setSelectedAlert(selectedRows.length ? selectedRows[0] : null)
    },
  }

  const onRowDataUpdatedDetails = (params) => {
    if (!params.api.getDisplayedRowCount()) {
      setSelectedAlert(null)
      return
    }

    const selectedNode = selectedAlert?.uuid ? params.api.getRowNode(selectedAlert.uuid) : null

    const nodeToSelect = selectedNode || params.api.getDisplayedRowAtIndex(0)

    if (nodeToSelect) {
      nodeToSelect.setSelected(true)
      params.api.ensureNodeVisible(nodeToSelect)
      setSelectedAlert(nodeToSelect.data)
    }
  }

  const IconForAlertType = useCallback(({ type }) => {
    let iconName = 'mdi:alert-circle-outline'
    switch (type) {
      case 'Data Acquisition':
        iconName = 'eos-icons:big-data-outlined'
        break
      case 'Anti-Collision':
        iconName = 'solar:target-linear'
        break
      case 'Well Notifications':
        iconName = 'mdi:bell-alert-outline'
        break
      case 'Drilling Parameters':
        iconName = 'mdi:gauge'
        break
      case 'Data Bridge':
        iconName = 'mdi:email-edit-outline'
        break
      default:
        iconName = 'mdi:alert-circle-outline'
    }
    return <Iconify icon={iconName} width={20} height={20} />
  }, [])

  const TypeCellRenderer = useCallback((params) => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          width: '100%',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
          <Badge
            badgeContent={params?.node?.data?.alerts?.length}
            sx={{
              '& .MuiBadge-badge': {
                top: 2,
                right: 2,
                backgroundColor: appColors.nptColor,
                fontSize: '.6rem',
                height: '14px',
                minWidth: '14px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              },
            }}>
            <IconForAlertType type={params.value} />
          </Badge>

          <Box>{params.value}</Box>
        </Box>
      </Box>
    )
  }, [])

  let columnDefs = useMemo(
    () => [
      {
        field: 'type',
        colId: 'type',
        headerName: 'Type',
        editable: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        cellRenderer: TypeCellRenderer,
      },
      {
        field: 'numberAlerts',
        colId: 'numberAlerts',
        headerName: '# Alerts',
        width: 150,
        cellStyle: centerAlignCell,
        valueGetter: (params) => params.data?.alerts?.length,
      },
      {
        field: 'activeDashboard',
        colId: 'activeDashboard',
        headerName: '# Dashboard',
        width: 150,
        cellStyle: centerAlignCell,
        valueGetter: (params) =>
          params.data?.alerts?.filter((alert) => alert.useDashboards && !alert.silenceAlert).length,
      },
      {
        field: 'activeEmail',
        colId: 'activeEmail',
        headerName: '# Email',
        width: 150,
        cellStyle: centerAlignCell,
        valueGetter: (params) => params.data?.alerts?.filter((alert) => alert.useEmail && !alert.silenceAlert).length,
      },
      {
        field: 'activeSms',
        colId: 'activeSms',
        headerName: '# SMS',
        width: 150,
        cellStyle: centerAlignCell,
        valueGetter: (params) => params.data?.alerts?.filter((alert) => alert.useSms && !alert.silenceAlert).length,
      },
      {
        field: 'silenced',
        colId: 'silenced',
        headerName: '# Silenced',
        width: 150,
        cellStyle: centerAlignCell,
        valueGetter: (params) => params.data?.alerts?.filter((alert) => alert.silenceAlert).length,
      },
    ],
    [TypeCellRenderer, centerAlignCell],
  )

  const columnDefsDetails = useMemo(() => {
    const columns = [
      {
        field: 'name',
        colId: 'name',
        headerName: 'Name',
        valueGetter: (params) => {
          if (!params.data?.unit) return params.data?.name
          if (params.data?.unit === 'Wob') {
            return `${params.data?.name} (${getUnitsText(UNITS_FOR.Depth)}/hr)`
          }

          return `${params.data?.name} (${getUnitsText(UNITS_FOR[params.data?.unit])})`
        },
        width: 200,
      },
      {
        headerName: 'Severity',
        field: 'severity',
        colId: 'severity',
        width: 150,
      },
      {
        field: 'phase',
        colId: 'phase',
        headerName: 'Phase',
        width: 200,
        minWidth: 200,
        maxWidth: 300,
      },
      {
        field: 'rig',
        colId: 'rig',
        headerName: 'Rig',
        width: 200,
        minWidth: 200,
        maxWidth: 300,
      },
    ]

    if (selectedAlertHeader?.type === 'Drilling Parameters' || selectedAlertHeader?.type === 'Anti-Collision') {
      columns.push(
        {
          field: 'min',
          colId: 'min',
          headerName: 'Min',
          cellStyle: centerAlignCell,
          valueFormatter: (p) => (!p.data?.useMin ? '--' : numberWithCommasDecimals(p.data?.min, 2)),
          width: 150,
        },
        {
          field: 'max',
          colId: 'max',
          headerName: 'Max',
          cellStyle: centerAlignCell,
          valueFormatter: (p) => (!p.data?.useMax ? '--' : numberWithCommasDecimals(p.data?.max, 2)),
          width: 150,
        },
        {
          field: 'minDepth',
          colId: 'minDepth',
          headerName: 'Min Depth',
          cellStyle: centerAlignCell,
          valueFormatter: (params) =>
            params.data?.useMinDepth ? numberWithCommasDecimals(params.data?.minDepth, 2) : '--',
          width: 150,
        },
        {
          field: 'maxDepth',
          colId: 'maxDepth',
          headerName: 'Max Depth',
          cellStyle: centerAlignCell,
          valueFormatter: (params) =>
            params.data?.useMaxDepth ? numberWithCommasDecimals(params.data?.maxDepth, 2) : '--',
          width: 150,
        },
      )
    }

    if (selectedAlertHeader?.type === 'Anti-Collision') {
      columns.push({
        field: 'ignoreProjections',
        colId: 'ignoreProjections',
        headerName: 'Include Projections',
        cellStyle: centerAlignCell,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        width: 130,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueGetter: (params) => !params.data?.ignoreProjections,
      })
    }

    columns.push(
      {
        field: 'useDashboards',
        colId: 'useDashboards',
        headerName: 'Dashboard',
        cellStyle: centerAlignCell,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
      },
      {
        field: 'useEmail',
        colId: 'useEmail',
        headerName: 'Email',
        cellStyle: centerAlignCell,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
      },
      {
        field: 'useSms',
        colId: 'useSms',
        headerName: 'SMS',
        cellStyle: centerAlignCell,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
      },
    )

    columns.push({
      field: 'silenceAlert',
      colId: 'silenceAlert',
      headerName: 'Silence Alert',
      cellStyle: centerAlignCell,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      width: 130,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
    })

    return columns
  }, [selectedAlertHeader?.type, getUnitsText, centerAlignCell])

  const getHeaders = () => {
    if (!alertData) return []
    if (!alertData.hasOwnProperty('notifications')) return []

    headersRef.current = alertData.notifications || []

    return headersRef.current
  }

  const getAlertDetails = () => {
    if (!alertData) return []
    if (!alertData.hasOwnProperty('notifications')) return []

    return selectedAlertHeader?.alerts || []
  }

  const mergeCopiedAlerts = useCallback(
    ({ mode, alerts }) => {
      if (!Array.isArray(alerts) || alerts.length === 0) {
        setCopyOpen(false)
        return
      }
      if (!Array.isArray(alertData?.notifications)) {
        setCopyOpen(false)
        return
      }

      const notifications = alertData.notifications

      const exists = (arr, a) => arr.some((x) => x.uuid === a.uuid)

      if (mode === 'overwrite') {
        for (const n of notifications) {
          n.alerts = []
          if (n?.type) refreshTypeCellForRow(n.type)
        }
      }

      for (const a of alerts) {
        const target = notifications.find((n) => n.type === a.type)
        if (!target) continue

        if (!Array.isArray(target.alerts)) target.alerts = []

        const newAlert = { ...a }
        delete newAlert.type
        newAlert.uuid = getAlertsKey(newAlert)

        if (!exists(target.alerts, newAlert)) {
          target.alerts.push(newAlert)
        }

        refreshTypeCellForRow(target.type)
      }

      setChangesMade(true)
      setCopyOpen(false)
    },
    [alertData, refreshTypeCellForRow, getAlertsKey],
  )

  const showResult = (res) => {
    setStatus({
      show: true,
      severity: res.success ? 'success' : 'error',
      message: res.message,
    })
  }

  const getRowIdDetails = useCallback((params) => params.data?.uuid || crypto.randomUUID(), [])

  const editAlert = useCallback(
    (updatedAlert) => {
      if (!selectedAlertHeader || !selectedAlert) return
      if (!Array.isArray(alertData?.notifications)) return

      const updatedAlertWithKey = {
        ...updatedAlert,
        uuid: getAlertsKey(updatedAlert),
      }

      const duplicateExists = (selectedAlertHeader.alerts || []).some(
        (alert) => alert.uuid !== selectedAlert.uuid && alert.uuid === updatedAlertWithKey.uuid,
      )

      if (duplicateExists) {
        setStatus({
          show: true,
          severity: 'error',
          message: 'An identical alert already exists.',
        })
        return
      }

      const nextAlerts = (selectedAlertHeader.alerts || []).map((alert) =>
        alert.uuid === selectedAlert.uuid ? updatedAlertWithKey : alert,
      )

      const nextHeader = {
        ...selectedAlertHeader,
        alerts: nextAlerts,
      }

      updateAlertHeader(nextHeader)
      setSelectedAlert(updatedAlertWithKey)

      refreshTypeCellForRow(nextHeader.type)
      gridApiDetails.current?.setGridOption('rowData', nextAlerts)
      updateDepthColumnVisibility(nextAlerts)
      gridApiDetails.current?.refreshCells({ force: true })

      setChangesMade(true)
      setAlertDialog({
        open: false,
        mode: 'add',
      })
    },
    [
      alertData,
      selectedAlertHeader,
      selectedAlert,
      refreshTypeCellForRow,
      getAlertsKey,
      updateAlertHeader,
      updateDepthColumnVisibility,
    ],
  )

  const addAlert = useCallback(
    (newAlerts) => {
      if (!selectedAlertHeader) return

      const existing = Array.isArray(selectedAlertHeader.alerts) ? selectedAlertHeader.alerts : []

      const toAdd = (newAlerts || [])
        .filter((a) => a?.name && a?.severity)
        .map((a) => ({ ...a, uuid: getAlertsKey(a) }))

      const addedAlerts = toAdd.filter((a) => !existing.some((x) => x.uuid === a.uuid))

      const nextAlerts = [...existing, ...addedAlerts]

      const nextHeader = {
        ...selectedAlertHeader,
        alerts: nextAlerts,
      }

      updateAlertHeader(nextHeader)
      setSelectedAlert(addedAlerts[addedAlerts.length - 1] || nextAlerts[0] || null)

      refreshTypeCellForRow(nextHeader.type)
      gridApiDetails.current?.setGridOption('rowData', nextAlerts)
      updateDepthColumnVisibility(nextAlerts)

      setChangesMade(true)
      setAlertDialog({
        open: false,
        mode: 'add',
      })
    },
    [refreshTypeCellForRow, getAlertsKey, selectedAlertHeader, updateDepthColumnVisibility, updateAlertHeader],
  )

  const handleClose = useCallback(() => {
    if (changesMade) {
      setConfirm({
        show: true,
        title: 'Discard Changes',
        text: 'You have unsaved alerts changes. Are you sure you want to close?',
        onConfirm: () => {
          setConfirm({ show: false })

          onCancel()
        },
      })

      return
    }

    onCancel()
  }, [changesMade, onCancel])

  return (
    <Dialog
      fullWidth
      maxWidth='xl'
      PaperProps={{
        sx: {
          backgroundColor: getWindowBarColor(),
          height: '75vh',
          width: '70vw',
          maxWidth: '95vw',
        },
      }}
      open={true}
      onClose={handleClose}>
      <ConfirmDialog
        title={confirm.title}
        open={confirm.show}
        setOpen={() =>
          setConfirm({
            show: false,
            title: '',
            text: '',
            onConfirm: null,
          })
        }
        onConfirm={confirm.onConfirm}>
        {confirm.text}
      </ConfirmDialog>

      {favoriteWellsOpen && (
        <FavoriteWellsModal user={userName} onClose={() => setFavoriteWellsOpen(false)} open={favoriteWellsOpen} />
      )}

      <AddAlertDialog
        open={alertDialog.open}
        onClose={() => {
          setAlertDialog({
            open: false,
            mode: 'add',
          })
        }}
        onSubmit={alertDialog.mode === 'edit' ? editAlert : addAlert}
        alertToEdit={alertDialog.mode === 'edit' ? selectedAlert : null}
        alertTypeDropDowns={selectedAlertHeader?.dropDowns || []}
        severityDropDowns={selectedAlertHeader?.infoOnly ? ['Info'] : ['Info', 'Warning', 'Critical']}
        phaseDropDowns={phaseData}
        rigDropDowns={rigData}
        defaultAlerts={selectedAlertHeader?.defaultAlerts || []}
        showProjections={selectedAlertHeader?.type === 'Anti-Collision'}
        alertCategory={selectedAlertHeader?.type || ''}
        smsAvailable={alertData?.smsAvailable ?? false}
      />
      <CopyAlertDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        onSubmit={mergeCopiedAlerts}
        alertUsers={alertUsers}
        alertRoles={alertRoles}
        alertGroups={alertGroups}
        sourceOptions={copySourceOptions}
      />
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Iconify icon='vaadin:bell' sx={{ marginRight: '10px' }} />
          Alerts - Only trigger for {!isGroupsMode && "'Favorite' (⭐) "}wells with an active well status
        </Typography>

        <IconButton onClick={handleClose} size='small' sx={{ ml: 1 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
      <Box
        sx={{
          height: 'calc(90vh - 44px)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <Box sx={{ p: 1 }}>
          <AlertsRibbon
            selectedAlertHeader={selectedAlertHeader}
            selectedAlert={selectedAlert}
            canAdd={canAdd}
            onAddAlert={onAddAlert}
            onDeleteAlert={onDeleteAlert}
            changesMade={changesMade}
            onSave={onSave}
            onDiscard={onDiscard}
            showCopyButton={showCopyButton}
            copyButtonLabel={copyButtonLabel}
            onCopyClick={() => setCopyOpen(true)}
            canEdit={getEditable(selectedAlert)}
            onEditAlert={onEditAlert}
            onShowFavorites={() => setFavoriteWellsOpen(true)}
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box
            className={getAgGridTheme()}
            sx={{
              height: '50%',
              width: '100%',
            }}>
            <AgGridReact
              rowData={loading ? [] : getHeaders()}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              headerHeight={30}
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              getRowId={(params) => params.data.type}
              loading={loading}
              rowSelection={{
                mode: 'singleRow',
                enableClickSelection: true,
                checkboxes: false,
              }}
            />
          </Box>
          <Box
            className={getAgGridTheme()}
            sx={{
              height: '50%',
              width: '100%',
            }}>
            <AgGridReact
              rowData={loading ? [] : getAlertDetails()}
              columnDefs={columnDefsDetails}
              defaultColDef={defaultColDefDetails}
              onGridReady={onGridReadyDetails}
              onFirstDataRendered={onFirstDataRenderedDetails}
              getRowId={getRowIdDetails}
              gridOptions={gridOptionsDetails}
              loading={loading}
              rowSelection={{
                mode: 'singleRow',
                enableClickSelection: true,
                checkboxes: false,
              }}
              onRowDataUpdated={onRowDataUpdatedDetails}
            />
          </Box>
        </Box>
      </Box>
      <StatusSnackbar status={status} setStatus={setStatus} />
    </Dialog>
  )
}

export default AlertsModal
